import { useRecoilState } from 'recoil';
import { missingInformationAtom } from '../../../recoil/atoms/missingInformationAtom';
import {
    AddressField,
    Box,
    CprField,
    EmailField,
    Grid,
    GridItem,
    PhoneField,
    validateCpr
} from '@flos/react-ui';
import { useHippoText } from '../../../hooks/useHippoText';
import { type FieldEventDataProps } from '@flos/react-ui/build/core/forms/address-field/address-field';
import { useState } from 'react';
import { getDawaIdFromkvhxUrl } from '../../../utils/addressUtil';
import { isContainingDuplicateCPR } from '../configs/ValidateMissingDTOConfig';
import { replaceAllCharactersButNumbers } from '../../../utils/stringUtil';
import CustomerStopModal from '../../../shared_components/CustomerStopModal/CustomerStopModal';
// eslint-disable-next-line max-len
import type { AccidentYoungAndOtherPersonMissingInformationDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/missingInformation';

const AccidentOtherPersonMissingInformationForm = ({
    accidentOtherPersonMissingInformation
}: {
    accidentOtherPersonMissingInformation: AccidentYoungAndOtherPersonMissingInformationDTO;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const [missingInformation, setMissingInformation] = useRecoilState(missingInformationAtom);
    const { cpr, fixedId, address, isDummyContact, fullName, flavourName, phoneNumber, email } =
        accidentOtherPersonMissingInformation;
    const [cprNr, setCprNr] = useState<string>('');
    const [showStopModal, setShowStopModal] = useState<boolean>(false);
    const isCprNrValid = cprNr ? validateCpr(cprNr) : undefined;
    const isDuplicate = isContainingDuplicateCPR(missingInformation.accidentOtherPerson ?? []);

    const updateMissingInformationProperty = (updateValue: object): void => {
        const updateValueWithFlavour = { flavourName, ...updateValue };
        setMissingInformation((previousState) => {
            const updateDto = previousState.accidentOtherPerson?.map(
                (item: AccidentYoungAndOtherPersonMissingInformationDTO) => {
                    if (item.fixedId !== fixedId) return item;
                    return {
                        ...item,
                        ...updateValueWithFlavour
                    };
                }
            );
            return {
                ...previousState,
                accidentOtherPerson: updateDto
            };
        });
    };

    const updateCpr = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const formattedCPR = replaceAllCharactersButNumbers(event.target.value);
        setCprNr(formattedCPR);
        if (accidentOtherPersonMissingInformation.cpr !== formattedCPR) {
            updateMissingInformationProperty({
                cpr: formattedCPR
            });
        }
    };

    const updateAddress = (fieldEventDataProps: FieldEventDataProps): void => {
        const { address, kvhxUrl, addressInfo } = fieldEventDataProps;

        //  Checking for address on Bornholm
        const bornholmZipcodeLeadingDigits = '37';
        if (addressInfo.zipCode.substring(0, 2) === bornholmZipcodeLeadingDigits) {
            updateMissingInformationProperty({ address: '', dawaId: '' });
            setShowStopModal(true);
        } else {
            const dawaId = getDawaIdFromkvhxUrl(kvhxUrl);
            updateMissingInformationProperty({ address, dawaId });
        }
    };

    return (
        <Grid gap={6}>
            <GridItem span={12}>
                <h6 className="u-spacing-stack-none">
                    {getHippoText('DSS-ADDITIONAL-INFORMATION-TITLE-SUBHEADER', {
                        coverable_name: fullName ?? ''
                    })}
                </h6>
            </GridItem>
            <FieldBox
                label={getHippoText('DSS-ADDITIONAL-INFORMATION-CPRNUMBER-SUBHEADER', {
                    coverable_name: fullName ?? ''
                })}
            >
                {' '}
                <CprField
                    label={getHippoText('DSS-ADDITIONAL-INFORMATION-CPRNUMBER')}
                    value={isDummyContact ? '' : cpr}
                    onBlur={updateCpr}
                    isValid={isCprNrValid && !isDuplicate}
                    errorText={
                        isDuplicate
                            ? getHippoText('DSS-ADDITIONAL-INFORMATION-DUPLICATE-CPR')
                            : undefined
                    }
                />
            </FieldBox>
            <FieldBox
                label={getHippoText('DSS-ADDITIONAL-INFORMATION-ADDRESS-SUBHEADER', {
                    coverable_name: fullName ?? ''
                })}
            >
                <AddressField
                    label={getHippoText('DSS-ADDITIONAL-INFORMATION-ADDRESS')}
                    defaultValue={isDummyContact ? '' : address}
                    fieldEvent={updateAddress}
                />
            </FieldBox>
            <FieldBox
                label={getHippoText('DSS-ADDITIONAL-INFORMATION-PHONEANDEMAIL-SUBHEADER', {
                    coverable_name: fullName ?? ''
                })}
                description={getHippoText('DSS-ADDITIONAL-INFORMATION-PHONEANDEMAIL-SUBSUBHEADER')}
            >
                <PhoneField
                    label={getHippoText('DSS-ADDITIONAL-INFORMATION-MOBILENUMBER')}
                    value={isDummyContact ? '' : phoneNumber}
                    onBlur={(event) => {
                        updateMissingInformationProperty({
                            phoneNumber: replaceAllCharactersButNumbers(event.target.value)
                        });
                    }}
                />
                <EmailField
                    label={getHippoText('DSS-ADDITIONAL-INFORMATION-EMAIL')}
                    value={isDummyContact ? '' : email}
                    onBlur={(event) => {
                        updateMissingInformationProperty({
                            email: event.target.value
                        });
                    }}
                />
            </FieldBox>
            <CustomerStopModal
                stopCause={'Bornholm'}
                showModal={showStopModal}
                closeModal={() => {
                    setShowStopModal(false);
                }}
            />
        </Grid>
    );
};

const FieldBox = ({
    label,
    description,
    children
}: {
    label: string;
    description?: string;
    children: React.ReactNode;
}): React.JSX.Element => {
    return (
        <GridItem span={12}>
            <Box>
                <GridItem span={12}>
                    <h6>{label}</h6>
                </GridItem>
                <Grid gap={4}>
                    {description && <GridItem span={12}>{description}</GridItem>}
                    <GridItem span={12}>{children}</GridItem>
                </Grid>
            </Box>
        </GridItem>
    );
};
export default AccidentOtherPersonMissingInformationForm;
